import React, {
  useCallback, useEffect, useMemo, useState, Fragment, ReactElement,
} from 'react';
import Menu from '../../../components/menu/menu.component';
import FlexContainer from '../../../components/flex-container/flex-container.component';
import CheckAccess from '../../../components/check-access/check-access.component';
import ErrorBlock from '../../../components/error-block/error-block.component';
import DropdownButton from '../../../components/dropdown-button/dropdown-button.component';
import MenuItemList from '../../../components/menu-item-list/menu-item-list.component';
import DealRejectModal from '../deal-reject-modal/deal-reject-modal.component';
import { DEAL_STATUS_EDIT } from '../../../constants/entity/permissions';
import { DEAL_STATUS_DEAD_DEAL } from '../../../constants/entity/deal-statuses';

const DealStatusDropdown: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    currentStatus,
    currentDealRejectReason,
    errorMessage,
    dealStatusList,
    dealRejectReasonList,
    onStatusChange,
  } = props;

  const [menuAnchor, setMenuAnchor]: [HTMLElement | null, Function] = useState(null);
  const [selectedStatus, setSelectedStatus]: [DealStatus | null, Function] = useState<StateValueDefault>(null);
  const [isDealRejectModalOpen, setIsDealRejectModalOpen]: [boolean, Function] = useState(false);

  const currentDealRejectReasonText: string = currentDealRejectReason ? ` (${currentDealRejectReason})` : '';

  const dealStatusEditPermissions: string[] = useMemo((): string[] => [DEAL_STATUS_EDIT], []);

  const openStatusMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setMenuAnchor(event.currentTarget);

    event.stopPropagation();
  }, []);

  const closeStatusMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setMenuAnchor(null);

    event.stopPropagation();
  }, []);

  const closeDealRejectModal: Function = useCallback((): void => {
    setIsDealRejectModalOpen(false);
  }, []);

  const statusChange: Function = useCallback((
    statusValue: string, rejectReason?: string, rejectReasonComment?: string,
  ): void => {
    if (onStatusChange) {
      onStatusChange(statusValue, rejectReason, rejectReasonComment);
    }

    dealStatusList.forEach((status: DealStatus): void => {
      if (status.name === statusValue) {
        setSelectedStatus(status);
      }
    });

    setIsDealRejectModalOpen(false);
  }, [dealStatusList]);

  const statusSelect: Function = useCallback((statusValue: string): void => {
    if (selectedStatus && statusValue !== selectedStatus.name) {
      if (statusValue === DEAL_STATUS_DEAD_DEAL) {
        setIsDealRejectModalOpen(true);
      } else {
        statusChange(statusValue);
      }
    }
  }, [dealStatusList, selectedStatus]);

  const menuItemDealStatusList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: dealStatusList,
    selectedValue: selectedStatus,
    onClick: (event: React.MouseEvent<HTMLInputElement, MouseEvent>, selectedStatus: DealStatus): void => {
      if (selectedStatus.name) {
        statusSelect(selectedStatus.name);
      }

      closeStatusMenu(event);
    },
  }), [dealStatusList, selectedStatus]);

  useEffect((): void => {
    if (currentStatus && dealStatusList
      && (!selectedStatus || (selectedStatus && selectedStatus.name !== currentStatus))) {
      dealStatusList.forEach((status: DealStatus): void => {
        if (status.name === currentStatus) {
          setSelectedStatus(status);
        }
      });
    }
  }, [currentStatus]);

  return (
    <Fragment>
      {
        !!selectedStatus && (
          <CheckAccess permissions={dealStatusEditPermissions}>
            <FlexContainer>
              <FlexContainer isInline>
                <FlexContainer isInline>
                  {selectedStatus.title}
                  {currentDealRejectReasonText}
                </FlexContainer>
                <FlexContainer isInline>
                  <DropdownButton onClick={openStatusMenu} />
                  <Menu menuAnchor={menuAnchor} onClose={closeStatusMenu}>
                    {menuItemDealStatusList}
                  </Menu>
                </FlexContainer>
              </FlexContainer>
              {
                !!errorMessage && (
                  <ErrorBlock errorMessage={errorMessage} />
                )
              }
            </FlexContainer>
          </CheckAccess>
        )
      }
      <DealRejectModal
        isOpen={isDealRejectModalOpen}
        dealRejectReasonList={dealRejectReasonList}
        onConfirm={statusChange}
        onCloseModal={closeDealRejectModal}
      />
    </Fragment>
  );
});

export default DealStatusDropdown;
